import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
import { publicImageSchema, shopProductCardSchema, shopProductSchema, storefrontHostSchema, } from "./storefront.js";
import { sectionsV2, shopSectionsV2 } from "./storefront-sections.js";
import { shopperSignalsSchema } from "./storefront-tracking.js";
/**
 * Pages of the online store (B-80, SF-12): the home page, custom pages and single-product landing pages, built from
 * fixed section types — plain text and IDs only, never HTML — edited as a draft and published.
 */
const tag = { tag: "storefront" };
const text = (max) => z.string().trim().max(max);
const link = z
    .string()
    .trim()
    .max(300)
    .regex(/^(\/[^\s]*|https:\/\/[^\s]+|tel:\+?\d+)?$/);
export const PAGE_KINDS = ["home", "custom", "landing"];
export const pageSlugSchema = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);
export const sectionSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("hero"),
        title: text(120),
        subtitle: text(300).default(""),
        buttonLabel: text(40).default(""),
        buttonHref: link.default(""),
        imageFileId: z.uuid().nullable().default(null),
    }),
    z.object({
        type: z.literal("products"),
        title: text(120).default(""),
        categoryId: z.uuid().nullable().default(null),
        sort: z.enum(["newest", "price_asc", "price_desc", "name"]).default("newest"),
        limit: z.int().min(2).max(24).default(8),
        /** ST-10: how the products are shown (grid, a swipe row, or menu rows for food shops). */
        layout: z.enum(["grid", "carousel", "menu"]).default("grid"),
        /** DG-08: only hard copies or only digital products (an eBooks shelf); `any` shows both. */
        format: z.enum(["any", "physical", "digital"]).default("any"),
    }),
    z.object({
        type: z.literal("imageText"),
        title: text(120),
        body: text(2000).default(""),
        imageFileId: z.uuid().nullable().default(null),
        imageSide: z.enum(["left", "right"]).default("left"),
    }),
    z.object({ type: z.literal("text"), title: text(120).default(""), body: text(5000) }),
    z.object({
        type: z.literal("faq"),
        title: text(120).default(""),
        items: z
            .array(z.object({ question: text(200), answer: text(1000) }))
            .min(1)
            .max(12),
    }),
    z.object({
        type: z.literal("testimonials"),
        title: text(120).default(""),
        items: z
            .array(z.object({ name: text(60), text: text(500) }))
            .min(1)
            .max(6),
    }),
    z.object({ type: z.literal("countdown"), title: text(120), endsAt: z.iso.datetime() }),
    /** Landing pages: the product sold on the page and an order form (COD without OTP, owner decision 2026-10-30). */
    z.object({ type: z.literal("orderForm"), productId: z.uuid(), title: text(120).default("") }),
    ...sectionsV2,
]);
export const pageSummarySchema = z.object({
    id: z.uuid(),
    kind: z.enum(PAGE_KINDS),
    slug: z.string(),
    title: z.string(),
    published: z.boolean(),
    updatedAt: z.iso.datetime(),
});
export const pageSchema = pageSummarySchema.extend({
    seoTitle: z.string(),
    seoDescription: z.string(),
    draft: z.array(sectionSchema),
    live: z.array(sectionSchema).nullable(),
    /** The shop address of the page (with a preview token for the draft). */
    url: z.string(),
    previewUrl: z.string(),
    version: z.int(),
});
export const listStorefrontPages = defineEndpoint({
    ...tag,
    name: "listStorefrontPages",
    method: "GET",
    path: "/v1/storefront-pages",
    access: { permission: "storefront.view" },
    summary: "The shop's pages (home, custom, landing)",
    status: 200,
    response: z.object({ data: z.array(pageSummarySchema) }),
});
export const createStorefrontPage = defineEndpoint({
    ...tag,
    name: "createStorefrontPage",
    method: "POST",
    path: "/v1/storefront-pages",
    access: { permission: "storefront.manage" },
    summary: "Create a page (one home page; landing pages count against the plan)",
    body: z.object({ kind: z.enum(PAGE_KINDS), slug: pageSlugSchema, title: text(120).min(1) }).strict(),
    status: 201,
    response: pageSchema,
    errors: ["CONFLICT", "PLAN_LIMIT_REACHED"],
    entitlement: "storefront.landing_pages.max",
});
export const getStorefrontPage = defineEndpoint({
    ...tag,
    name: "getStorefrontPage",
    method: "GET",
    path: "/v1/storefront-pages/{id}",
    access: { permission: "storefront.view" },
    summary: "One page with its draft and live sections",
    params: z.object({ id: z.uuid() }),
    status: 200,
    response: pageSchema,
});
export const saveStorefrontPageSchema = z
    .object({
    title: text(120).min(1),
    slug: pageSlugSchema,
    seoTitle: text(70).default(""),
    seoDescription: text(170).default(""),
    draft: z.array(sectionSchema).max(30),
    version: z.int().min(1),
})
    .strict();
export const saveStorefrontPage = defineEndpoint({
    ...tag,
    name: "saveStorefrontPage",
    method: "PUT",
    path: "/v1/storefront-pages/{id}",
    access: { permission: "storefront.manage" },
    summary: "Save a page's draft and texts",
    params: z.object({ id: z.uuid() }),
    body: saveStorefrontPageSchema,
    status: 200,
    response: pageSchema,
    errors: ["CONFLICT"],
});
export const publishStorefrontPage = defineEndpoint({
    ...tag,
    name: "publishStorefrontPage",
    method: "POST",
    path: "/v1/storefront-pages/{id}/publish",
    access: { permission: "storefront.manage" },
    summary: "Make the draft live (published: false takes the page offline)",
    params: z.object({ id: z.uuid() }),
    body: z.object({ published: z.boolean(), version: z.int().min(1) }).strict(),
    status: 200,
    response: pageSchema,
    errors: ["CONFLICT"],
});
// Public ------------------------------------------------------------------------------------------------------------
/** A section as the storefront renders it: images as public URLs, product sections with their cards. */
export const shopSectionSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("hero"),
        title: z.string(),
        subtitle: z.string(),
        buttonLabel: z.string(),
        buttonHref: z.string(),
        image: publicImageSchema.nullable(),
    }),
    z.object({
        type: z.literal("products"),
        title: z.string(),
        categoryId: z.uuid().nullable(),
        products: z.array(shopProductCardSchema),
        layout: z.enum(["grid", "carousel", "menu"]).default("grid"),
        format: z.enum(["any", "physical", "digital"]).default("any"),
    }),
    z.object({
        type: z.literal("imageText"),
        title: z.string(),
        body: z.string(),
        image: publicImageSchema.nullable(),
        imageSide: z.enum(["left", "right"]),
    }),
    z.object({ type: z.literal("text"), title: z.string(), body: z.string() }),
    z.object({
        type: z.literal("faq"),
        title: z.string(),
        items: z.array(z.object({ question: z.string(), answer: z.string() })),
    }),
    z.object({
        type: z.literal("testimonials"),
        title: z.string(),
        items: z.array(z.object({ name: z.string(), text: z.string() })),
    }),
    z.object({ type: z.literal("countdown"), title: z.string(), endsAt: z.iso.datetime() }),
    z.object({ type: z.literal("orderForm"), title: z.string(), product: shopProductSchema.nullable() }),
    ...shopSectionsV2,
]);
export const shopPageSchema = z.object({
    kind: z.enum(PAGE_KINDS),
    slug: z.string(),
    title: z.string(),
    seoTitle: z.string(),
    seoDescription: z.string(),
    sections: z.array(shopSectionSchema),
    preview: z.boolean(),
});
export const getShopPage = defineEndpoint({
    ...tag,
    name: "getShopPage",
    method: "GET",
    path: "/v1/storefront/pages/{slug}",
    access: { public: true },
    summary: "A published page (`home` for the home page), or its draft with a preview token",
    params: z.object({ slug: z.string().min(1).max(60) }),
    query: z.object({ host: storefrontHostSchema, preview: z.string().max(200).optional() }),
    status: 200,
    response: shopPageSchema,
});
export const placeLandingOrderSchema = z
    .object({
    host: storefrontHostSchema,
    pageSlug: pageSlugSchema,
    checkoutId: z.uuid(),
    captcha: z.string().trim().min(1).max(2048),
    ip: z.string().max(64).default(""),
    variantId: z.uuid(),
    quantity: z.int().min(1).max(20),
    name: z.string().trim().min(1).max(200),
    phone: z.string().trim().min(10).max(20),
    districtId: z.int().min(1),
    upazilaId: z.int().min(1).nullable().default(null),
    addressLine: z.string().trim().min(3).max(300),
    note: z.string().trim().max(500).default(""),
    /** B-82: Meta cookies and user agent for the Conversions API. */
    signals: shopperSignalsSchema.optional(),
})
    .strict();
/**
 * ST-17 (single_product "Launch"): the order form's live total — the chosen pack × quantity and the delivery charge to
 * the district, priced by the server exactly as the order would be (no money math in the browser).
 */
export const quoteLandingOrderSchema = z
    .object({
    host: storefrontHostSchema,
    pageSlug: pageSlugSchema,
    variantId: z.uuid(),
    quantity: z.int().min(1).max(20),
    districtId: z.int().min(1),
    upazilaId: z.int().min(1).nullable().default(null),
})
    .strict();
export const landingQuoteSchema = z.object({
    subtotalPaisa: z.int(),
    /** The shop's automatic offer, if one applies (the order gets the same). */
    discountPaisa: z.int(),
    deliveryChargePaisa: z.int(),
    totalPaisa: z.int(),
});
export const quoteLandingOrder = defineEndpoint({
    ...tag,
    name: "quoteLandingOrder",
    method: "POST",
    path: "/v1/storefront/landing-orders/quote",
    access: { public: true },
    summary: "The total of a landing-page order before it is placed: pack × quantity and delivery to the district",
    body: quoteLandingOrderSchema,
    status: 200,
    response: landingQuoteSchema,
    errors: ["VARIANT_NOT_SELLABLE"],
});
export const placeLandingOrder = defineEndpoint({
    ...tag,
    name: "placeLandingOrder",
    method: "POST",
    path: "/v1/storefront/landing-orders",
    access: { public: true },
    summary: "Cash-on-delivery order from a landing page (no OTP; bot check, rate limits; waits for the shop's call)",
    body: placeLandingOrderSchema,
    status: 201,
    response: z.object({
        orderId: z.uuid(),
        number: z.string(),
        totalPaisa: z.int(),
        deliveryChargePaisa: z.int(),
    }),
    errors: ["BOT_CHECK_FAILED", "RATE_LIMITED", "VARIANT_NOT_SELLABLE", "BUSINESS_RULE_VIOLATION"],
});
//# sourceMappingURL=storefront-pages.js.map